import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AfricaNavbar from "@/components/AfricaNavbar";
import AfricaFooter from "@/components/AfricaFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, CheckCircle2, ChevronRight, Copy, MapPin, Send } from "lucide-react";
import { AFRICAN_COUNTRIES } from "@/lib/countries";

/**
 * Country request portal — the standard front door for every African country
 * except Zimbabwe. Instead of a marketplace, users submit an Independent
 * Verification Request that is captured server-side (TV-AFR-XXXXXXXX
 * reference) and routed to THOM Verify Operations.
 */

const CATEGORIES = [
  "Vehicle Verification", "Property Verification", "Equipment Verification",
  "Machinery Verification", "Commodity Verification", "Mining Verification",
  "Agricultural Verification", "Business Verification", "Supplier Due Diligence",
  "Identity Verification", "Corporate Verification", "Infrastructure Verification",
  "Fleet Verification", "Construction Verification", "Investment Due Diligence",
  "Transaction Assurance", "Digital Asset Passport", "Escrow & Transaction Management",
  "Bespoke Independent Verification",
];

const INDUSTRIES = [
  "Banking & Finance", "Insurance", "Government & Regulators", "Real Estate",
  "Mining & Industrial", "Energy", "Agriculture", "Automotive & Dealerships",
  "Logistics · Trade · Customs", "Corporate Procurement", "Luxury Assets",
  "Fleet Operators", "Individuals",
];

export default function AfricaCountryRequest() {
  const { iso } = useParams();
  const navigate = useNavigate();
  const country = useMemo(
    () => AFRICAN_COUNTRIES.find(c => c.iso === (iso || "").toUpperCase()),
    [iso]
  );

  const [form, setForm] = useState({
    country: country?.name || "",
    province: "", city: "", industry: "", category: "", asset_type: "",
    service_required: "", organisation: "", contact_name: "", telephone: "",
    email: "", asset_location: "", description: "",
    preferred_completion_date: "", urgency: "normal", estimated_value_usd: "",
    special_instructions: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { reference, status } after submission

  useEffect(() => {
    if (country) setForm(f => ({ ...f, country: country.name }));
  }, [country]);

  if (!country) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <AfricaNavbar />
        <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
          <h1 className="display text-4xl">Country not found</h1>
          <p className="text-white/70 mt-4">The country code <code className="mono text-[#B08C4A]">{iso}</code> is not supported yet.</p>
          <div className="mt-6"><Link to="/africa/countries"><Button className="bg-[#B08C4A] text-black hover:bg-[#8f6f34]">Back to countries</Button></Link></div>
        </div>
        <AfricaFooter />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        estimated_value_usd: form.estimated_value_usd ? Number(form.estimated_value_usd) : null,
      };
      const { data } = await api.post("/verification-requests", payload);
      setResult(data);
      toast.success(`Received · ${data.reference}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <AfricaNavbar />
        <section className="max-w-[1100px] mx-auto px-6 py-16 lg:py-24" data-testid="request-success">
          <div className="border border-[#B08C4A]/40 bg-white/[0.03] p-8 lg:p-12">
            <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            <div className="kicker text-white/60 mt-6">Verification request received</div>
            <h1 className="display text-4xl lg:text-5xl mt-2">Thank you — we're on it.</h1>
            <p className="mt-4 text-white/75 text-[15px] leading-[1.75] max-w-[640px]">
              Our regional operations team for <b className="text-white">{country.name}</b> will
              contact you within one business day. Your reference is:
            </p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <div className="border border-white/15 bg-black/40 px-4 py-3 mono uppercase tracking-[0.22em] text-lg text-[#B08C4A]" data-testid="request-ref">{result.reference}</div>
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => { navigator.clipboard.writeText(result.reference); toast.success("Reference copied"); }}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy reference
              </Button>
              <Link to={`/track-verification?ref=${result.reference}`} data-testid="request-track-link">
                <Button size="sm" className="bg-white text-black hover:bg-white/90">Track your request <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
              </Link>
            </div>
            <PipelineDisplay stages={result.pipeline_stages || []} current="received" />

            <div className="mt-8">
              <DocumentUploader reference={result.reference} />
            </div>

            <div className="mt-8 flex gap-2 flex-wrap">
              <Link to="/africa/countries"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to countries</Button></Link>
              <Button onClick={() => { setResult(null); setForm(f => ({ ...f, description: "", special_instructions: "" })); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black">Submit another request</Button>
            </div>
          </div>
        </section>
        <AfricaFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AfricaNavbar />
      <section className="max-w-[1500px] mx-auto px-6 py-14 lg:py-20">
        {/* Breadcrumb */}
        <nav className="text-[12px] text-white/50 mb-6">
          <Link to="/africa" className="hover:text-white">Africa</Link>
          <span className="mx-1.5">/</span>
          <Link to="/africa/countries" className="hover:text-white">Countries</Link>
          <span className="mx-1.5">/</span>
          <span className="text-white/70">THOM Verify {country.name}</span>
        </nav>

        <div className="grid lg:grid-cols-[1fr_1.35fr] gap-12">
          {/* Left column — country identity + workflow */}
          <div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#B08C4A]" />
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-white/60">{country.iso} · {country.currency}</span>
              <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#B08C4A]/15 text-[#D4B278]">Launching soon</span>
            </div>
            <h1 className="display text-4xl lg:text-6xl mt-4 leading-[1.02]" data-testid="request-country-title">THOM Verify™ {country.name}</h1>
            <p className="mt-5 text-white/75 text-[15px] leading-[1.75]">
              THOM Verify™ has not yet launched full commercial operations in {country.name}.
              Enterprise clients — banks, insurers, governments, dealerships, mining operators
              and industrial platforms — can commission bespoke independent verification
              assignments today. Complete the form and our regional operations team will contact
              you within one business day.
            </p>

            <div className="mt-8 border border-white/10 bg-white/[0.02] p-5">
              <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/60">Marketplace</div>
              <p className="text-[12.5px] text-white/70 mt-2">Marketplace coming soon in {country.name}. For live listings today, visit <Link to="/zw" className="text-[#B08C4A] hover:underline">THOM Verify Zimbabwe</Link>.</p>
            </div>

            <div className="mt-8">
              <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/60 mb-3">Workflow</div>
              <PipelineDisplay
                stages={["received","review","proposal","quotation","accepted","planning","field_verification","evidence_review","report_issued","completed"]}
                current={null}
              />
            </div>
          </div>

          {/* Right column — form */}
          <form onSubmit={submit} className="border border-white/15 bg-white/[0.03] p-6 lg:p-8 space-y-5" data-testid="request-form">
            <div>
              <div className="kicker text-white/60">Request independent verification</div>
              <h2 className="text-2xl font-semibold mt-1">Tell us what you need to verify in {country.name}.</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Country" required><Input value={form.country} disabled className="ent-input" data-testid="rq-country" /></Field>
              <Field label="Province / State"><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="ent-input" data-testid="rq-province" /></Field>
              <Field label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="ent-input" data-testid="rq-city" /></Field>
              <Field label="Industry">
                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                  <SelectTrigger className="ent-input" data-testid="rq-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Verification category" required>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="ent-input" data-testid="rq-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="max-h-[240px]">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Asset type"><Input value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })} className="ent-input" data-testid="rq-asset-type" placeholder="e.g. Toyota Land Cruiser · 3-bed apartment · 50-tonne excavator" /></Field>
              <Field label="Service required"><Input value={form.service_required} onChange={(e) => setForm({ ...form, service_required: e.target.value })} className="ent-input" data-testid="rq-service" placeholder="Short summary of the service" /></Field>
              <Field label="Estimated asset value (USD)"><Input type="number" min="0" step="any" value={form.estimated_value_usd} onChange={(e) => setForm({ ...form, estimated_value_usd: e.target.value })} className="ent-input" data-testid="rq-value" /></Field>
              <Field label="Organisation name"><Input value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="ent-input" data-testid="rq-org" /></Field>
              <Field label="Contact person" required><Input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="ent-input" data-testid="rq-contact" /></Field>
              <Field label="Telephone"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="ent-input" data-testid="rq-phone" /></Field>
              <Field label="Email" required><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ent-input" data-testid="rq-email" /></Field>
              <Field label="Preferred completion date"><Input type="date" value={form.preferred_completion_date} onChange={(e) => setForm({ ...form, preferred_completion_date: e.target.value })} className="ent-input" data-testid="rq-date" /></Field>
              <Field label="Urgency">
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger className="ent-input" data-testid="rq-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="priority">Priority</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Asset location">
              <Input value={form.asset_location} onChange={(e) => setForm({ ...form, asset_location: e.target.value })} className="ent-input" data-testid="rq-asset-location" placeholder="Physical address or coordinates where verification will happen" />
            </Field>

            <Field label="Description of verification required" required>
              <textarea
                required rows={4} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/40 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-white/40 focus:outline-none"
                placeholder="What needs to be verified? What is the intended use of the findings? Any deadlines?"
                data-testid="rq-description"
              />
            </Field>

            <Field label="Special instructions">
              <textarea
                rows={3} value={form.special_instructions}
                onChange={(e) => setForm({ ...form, special_instructions: e.target.value })}
                className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/40 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-white/40 focus:outline-none"
                data-testid="rq-notes"
              />
            </Field>

            <Button type="submit" disabled={busy} className="w-full bg-[#B08C4A] hover:bg-[#8f6f34] text-black h-11" data-testid="rq-submit">
              {busy ? "Submitting…" : (<><Send className="h-4 w-4 mr-2" /> Submit Verification Request</>)}
            </Button>
            <p className="text-[11px] text-white/50 text-center">Every submission generates a TV-AFR reference and is auto-routed to THOM Verify Operations for regional allocation.</p>
          </form>
        </div>
      </section>
      <AfricaFooter />

      <style>{`
        .ent-input { background: rgba(255,255,255,0.05) !important; border-color: rgba(255,255,255,0.2) !important; color: #fff !important; }
        .ent-input::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">
      {label}{required ? " *" : ""}
    </label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const STAGE_LABEL = {
  received: "Received",
  review: "Review",
  proposal: "Proposal",
  quotation: "Quotation",
  accepted: "Accepted",
  planning: "Planning",
  field_verification: "Field Verification",
  evidence_review: "Evidence Review",
  report_issued: "Report Issued",
  completed: "Completed",
};

const PipelineDisplay = ({ stages, current }) => {
  const idx = current ? stages.indexOf(current) : -1;
  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-1.5" data-testid="pipeline-display">
      {stages.map((s, i) => {
        const done = idx >= 0 && i <= idx;
        return (
          <div key={s} className={`border ${done ? "border-[#B08C4A]/60 bg-[#B08C4A]/10" : "border-white/10 bg-white/[0.02]"} p-2.5`}>
            <div className="mono text-[9px] uppercase tracking-widest text-white/50">Stage {i + 1}</div>
            <div className={`text-[11.5px] mt-1 ${done ? "text-[#D4B278]" : "text-white/80"}`}>{STAGE_LABEL[s] || s}</div>
          </div>
        );
      })}
    </div>
  );
};
